import { generateTimestamp, resizeImage } from '@/utils';
import { UploadToS3, UploadToS3Response } from './types';
import { ValidDocTypes, ValidMimeTypes } from './enums';
import { IMGS_MYME_TYPE } from './constants';
import { userMessageForS3Error } from './utils';
import { getPresignedUrl } from '../../workersServiceAPI/s3';

export const uploadToS3 = async ({
  acl,
  dir_name,
  doc_type,
  file,
  file_name,
  source_key = 'turn_admin',
  turn_id,
}: UploadToS3): Promise<UploadToS3Response> => {
  // Validate turn_id
  if (!turn_id || turn_id === '') {
    throw new Error('turn_id is required');
  }

  // Validate file mime types
  if (!Object.values(ValidMimeTypes).includes(file.type as ValidMimeTypes)) {
    throw new Error('Invalid file type');
  }

  // Validate doc type
  if (!Object.values(ValidDocTypes).includes(doc_type as ValidDocTypes)) {
    throw new Error('Invalid doc type');
  }

  // Create file name using the format {dir_name}/{id}__{doc_type}__{file_name}@{timestamp}.{file_ext}
  const timestamp = generateTimestamp();
  const fileExt = file.name.split('.').pop();
  const _dir_name = dir_name ? `${dir_name}/` : '';
  const _file_name = file_name ? `__${file_name.replace(' ', '_')}` : '';
  const newFileName = `${_dir_name}${turn_id}__${doc_type}${_file_name}@${timestamp}.${fileExt}`;

  // Resize file if is an image
  const optimizedFile = IMGS_MYME_TYPE.includes(file.type as ValidMimeTypes) ? await resizeImage(file) : file;

  // 1) Ask server for presigned PUT URL
  let presignRes: GetPresignedUrl.Response;
  try {
    presignRes = await getPresignedUrl({
      acl,
      content_type: optimizedFile.type,
      key: newFileName,
      source_key,
    });
  } catch (err) {
    throw new Error('Could not reach upload service. Check your connection and try again.');
  }

  const { url, object_url } = presignRes;

  if (!url) {
    console.error('Presign failed with error; ', presignRes);
    throw new Error('Upload service returned an invalid presigned URL.');
  }

  // 2) Upload file directly to S3
  let uploadRes: Response;
  try {
    uploadRes = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': optimizedFile.type,
        'x-amz-acl': acl,
      },
      body: optimizedFile,
    });
  } catch (err) {
    throw new Error('Files upload failed due to a network error. Please try again.');
  }

  if (!uploadRes.ok) {
    const text = await uploadRes.text().catch(() => '');
    const normalizedErrorMessage = userMessageForS3Error(uploadRes.status, text);

    console.error('S3 upload failed', {
      status: uploadRes.status,
      body: text,
    });

    throw new Error(normalizedErrorMessage);
  }

  return {
    key: newFileName,
    location: object_url,
  };
};
